/* eslint-disable indent */
import {useEffect, useState} from 'react';
import {useParams, useNavigate, Link} from 'react-router-dom';
import {MapContainer, TileLayer, Marker, Popup} from 'react-leaflet';
import L from 'leaflet';
import {format} from 'date-fns';
import toast from 'react-hot-toast';
import {getParcel, deleteParcel} from '../../api/parcels';
import {getMeasurements} from '../../api/measurements';
import {getCurrentWeather} from '../../api/weather';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import {FullPageSpinner} from '../../components/common/Spinner';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function ParcelDetail() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [parcel, setParcel] = useState(null);
    const [measurements, setMeasurements] = useState([]);
    const [weather, setWeather] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        Promise.all([
            getParcel(id),
            getMeasurements({parcel: id}),
            getCurrentWeather(id).catch(() => null),
        ])
            .then(([pRes, mRes, wRes]) => {
                setParcel(pRes.data);
                setMeasurements(mRes.data?.results ?? mRes.data);

                const rawWeather = wRes?.data?.current ?? wRes?.data;

                setWeather(
                    rawWeather
                        ? {
                            temperature: rawWeather.temp_c,
                            description: rawWeather.condition?.text ?? '',
                            humidity: rawWeather.humidity,
                            wind_speed:
                                rawWeather.wind_kph != null
                                    ? (rawWeather.wind_kph / 3.6).toFixed(1)
                                    : null,
                        }
                        : null
                );
            })
            .catch(() => {
                toast.error('Неуспешно вчитување на парцелата');
                navigate('/parcels');
            })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteParcel(id);
            toast.success('Парцелата е избришана');
            navigate('/parcels');
        } catch {
            toast.error('Неуспешно бришење');
            setDeleting(false);
        }
    };

    if (loading || !parcel) return <FullPageSpinner/>;

    const lat = parseFloat(parcel.latitude);
    const lng = parseFloat(parcel.longitude);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/parcels" className="text-sm text-gray-500 hover:text-green-700">
                        &larr; Назад кон парцелите
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-800 mt-1">{parcel.parcel_name}</h1>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate(`/parcels/${id}/edit`)}>Уреди</Button>
                    <Button variant="danger" onClick={() => setShowDeleteModal(true)}>Избриши</Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <h2 className="font-semibold text-gray-700 mb-4">Карта на локацијата</h2>
                        <div className="rounded-lg overflow-hidden h-64">
                            <MapContainer center={[lat, lng]} zoom={13} style={{height: '100%', width: '100%'}}>
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <Marker position={[lat, lng]}>
                                    <Popup>{parcel.parcel_name}</Popup>
                                </Marker>
                            </MapContainer>
                        </div>
                    </Card>

                    <Card>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-semibold text-gray-700">Последни мерења</h2>
                            <Link to="/measurements" className="text-sm text-green-700 hover:underline">Прегледај ги сите</Link>
                        </div>
                        {measurements.length === 0 ? (
                            <p className="text-gray-400 text-sm text-center py-8">Нема запишани мерења.</p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="border-b border-gray-100">
                                    {['Датум', 'Влажност %', 'pH', 'Темп °C'].map((h) => (
                                        <th key={h} className="text-left py-2 text-gray-500 font-medium">{h}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {measurements.slice(0, 8).map((m) => (
                                    <tr key={m.id} className="border-b border-gray-50">
                                        <td className="py-2 text-gray-500 text-xs">{format(new Date(m.measured_at), 'MMM dd, HH:mm')}</td>
                                        <td className="py-2">{m.soil_moisture ?? '—'}</td>
                                        <td className="py-2">{m.ph_value ?? '—'}</td>
                                        <td className="py-2">{m.soil_temperature ?? '—'}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </Card>
                </div>

                <div className="space-y-4">
                    <Card>
                        <h2 className="font-semibold text-gray-700 mb-4">Информации за парцелата</h2>
                        <dl className="space-y-3 text-sm">
                            <div className="flex justify-between">
                                <dt className="text-gray-500">Култура</dt>
                                <dd><Badge color="green">{parcel.crop_type || '—'}</Badge></dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">Површина</dt>
                                <dd className="font-medium">{parcel.size_m2} м²</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">Геогр. ширина</dt>
                                <dd className="font-mono text-xs">{lat.toFixed(5)}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">Геогр. должина</dt>
                                <dd className="font-mono text-xs">{lng.toFixed(5)}</dd>
                            </div>
                        </dl>
                    </Card>

                    {weather && (
                        <Card>
                            <h2 className="font-semibold text-gray-700 mb-4">Тековна временска состојба</h2>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Состојба</span>
                                    <span
                                        className="font-medium capitalize">{weather.description || weather.condition}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Температура</span>
                                    <span className="font-medium">{weather.temperature}°C</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Влажност</span>
                                    <span className="font-medium">{weather.humidity}%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Ветер</span>
                                    <span className="font-medium">{weather.wind_speed} м/с</span>
                                </div>
                            </div>
                        </Card>
                    )}

                    <Card>
                        <h2 className="font-semibold text-gray-700 mb-3">Брзи акции</h2>
                        <div className="space-y-2">
                            <Link to={`/chat?parcel=${id}`} className="block">
                                <Button size="sm" className="w-full">Отвори AI разговор</Button>
                            </Link>
                            <Link to="/measurements" className="block">
                                <Button variant="outline" size="sm" className="w-full">+ Додај мерење</Button>
                            </Link>
                            <Link to="/weather" className="block">
                                <Button variant="outline" size="sm" className="w-full">Прегледај прогноза</Button>
                            </Link>
                        </div>
                    </Card>
                </div>
            </div>

            <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Избриши парцела" size="sm">
                <p className="text-gray-600 text-sm mb-5">
                    Дали сте сигурни дека сакате да ја избришете <strong>{parcel.parcel_name}</strong>?
                </p>
                <div className="flex gap-3 justify-end">
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Откажи</Button>
                    <Button variant="danger" loading={deleting} onClick={handleDelete}>Избриши</Button>
                </div>
            </Modal>
        </div>
    );
}
